<template>
  <aside 
    class="menu-component" 
    :class="{ 'menu-collapsed': !isMenuOpen && !isDesktop }"
    :style="menuBackground ? { backgroundImage: `url(${menuBackground})` } : {}"
  >
    <nav class="menu-nav">
      <ul class="menu-list">
        <li 
          v-for="menu in menuList" 
          :key="menu.path || menu.menu_id" 
          class="menu-item"
          :class="{ 'active': $route.path === menu.path, 'parent-item': menu.isParent }"
        >
          <!-- 父菜单项 -->
          <div 
            v-if="menu.isParent" 
            class="menu-link parent-link"
            @click="toggleSubMenu(menu)"
          >
            <i :class="menu.icon"></i>
            <span class="menu-text">{{ menu.name }}</span>
            <i 
              class="el-icon-arrow-right menu-arrow"
              :class="{ 'rotate': isSubMenuOpen(menu) }"
            ></i>
          </div>
          <!-- 子菜单项 -->
          <router-link 
            v-else 
            :to="menu.path" 
            class="menu-link"
            @click.native="handleMenuClick"
          >
            <i :class="menu.icon"></i>
            <span class="menu-text">{{ menu.name }}</span>
          </router-link>
          
          <!-- 子菜单列表 -->
          <ul 
            v-if="menu.isParent && menu.children && menu.children.length > 0" 
            class="sub-menu-list"
            :class="{ 'show': isSubMenuOpen(menu) }"
          >
            <li 
              v-for="subMenu in menu.children" 
              :key="subMenu.path || subMenu.menu_id" 
              class="sub-menu-item"
              :class="{ 'active': $route.path === subMenu.path }"
            >
              <router-link :to="subMenu.path" class="sub-menu-link" @click.native="handleMenuClick">
                <i :class="subMenu.icon"></i>
                <span class="sub-menu-text">{{ subMenu.name }}</span>
              </router-link>
            </li>
          </ul>
        </li>
      </ul>
    </nav>
  </aside>
</template>

<script>
// 侧边栏菜单 = 登录后 /admin/menu 接口下发（Vuex store.menuList），刷新由 initUserInfo 从 localStorage 恢复
export default {
  name: 'MenuComponent',
  props: {
    isDesktop: {
      type: Boolean,
      default: true
    },
    isMenuOpen: {
      type: Boolean,
      default: true
    }
  },
  data() {
    return {
      // 菜单背景图片（留空则用组件自带的深色底）
      menuBackground: '',
      // 存储当前展开的子菜单ID
      openSubMenus: []
    }
  },
  computed: {
    // 侧边栏菜单：登录接口成功后并行拉取的 /admin/menu 返回值（store.buildMenuList 已映射 path/name/icon/isParent/children）
    menuList() {
      return this.$store.state.menuList
    }
  },
  watch: {
    // 刷新或直接输入地址进入时，自动展开当前路由所在的父菜单
    '$route.path': {
      immediate: true,
      handler(path) {
        this.menuList.forEach(menu => {
          if (menu.children && menu.children.some(sub => sub.path === path)) {
            if (!this.openSubMenus.includes(menu.path)) this.openSubMenus.push(menu.path)
          }
        })
      }
    }
  },
  methods: {
    // 切换子菜单展开状态
    toggleSubMenu(menu) {
      const menuId = menu.path || menu.menu_id;
      const index = this.openSubMenus.indexOf(menuId);
      if (index > -1) {
        // 如果已展开，则关闭
        this.openSubMenus.splice(index, 1);
      } else {
        // 如果未展开，则打开
        this.openSubMenus.push(menuId);
      }
    },
    // 检查子菜单是否展开
    isSubMenuOpen(menu) {
      const menuId = menu.path || menu.menu_id;
      return this.openSubMenus.includes(menuId);
    },
    // 处理菜单项点击事件
    handleMenuClick() {
      // 如果是手机端，点击菜单项后关闭菜单
      if (!this.isDesktop) {
        this.$emit('menu-click');
      }
    }
  }
}
</script>

<style scoped>
/* 使用全局样式中定义的 CSS 变量 */
.menu-component {
  background-color: var(--menu-bg);
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  color: white;
  height: 100%;
  overflow-y: auto;
  transition: width 0.3s ease;
  overflow: hidden;
  position: relative;
  box-shadow: var(--shadow-sm);
  display: flex;
  flex-direction: column;
}
/* 背景遮罩：仅在设置了背景图时压暗，保证文字可读性 */
.menu-component::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.25);
  z-index: 0;
}

/* 电脑端菜单样式 */
.menu-component:not(.menu-collapsed) {
  width: 200px;
}

/* 手机端菜单样式 */
.menu-component.menu-collapsed {
  width: 0;
}

.menu-nav {
  height: 100%;
  box-sizing: border-box;
  padding: 20px 0;
  flex: 1;
  display: flex;
  flex-direction: column;
  position: relative;
  z-index: 1;
  overflow-y:  auto;
}

.menu-list {
  list-style: none;
  margin: 0;
  padding: 0;
  flex: 1;
  min-height: 100%; /* 确保列表高度至少为100% */
}

.menu-item {
  margin: 0;
  padding: 0;
}

.menu-item.active .menu-link {
  background-color: rgba(142, 17, 3, 0.55);
  color: #fff;
  box-shadow: inset 3px 0 0 var(--primary-200);
}

.menu-link {
  display: flex;
  align-items: center;
  padding: 12px 20px;
  color: rgba(255, 255, 255, 0.9);
  text-decoration: none;
  transition: all 0.3s ease;
  white-space: nowrap;
}

.menu-link:hover {
  background-color: rgba(255, 255, 255, 0.15);
  color: white;
}

.menu-link i {
  font-size: 16px;
  margin-right: 10px;
  color: rgba(255, 255, 255, 0.9);
}

.menu-link:hover i {
  color: white;
}

/* 菜单文字样式 */
.menu-text {
  font-size: 14px;
  flex: 1;
}

/* 菜单箭头样式 */
.menu-arrow {
  font-size: 12px;
  margin-left: auto;
  color: rgba(255, 255, 255, 0.7);
  transition: transform 0.3s ease;
}

.menu-arrow.rotate {
  transform: rotate(90deg);
}

/* 父菜单项样式 */
.parent-link {
  cursor: pointer;
}

/* 子菜单列表样式 */
.sub-menu-list {
  list-style: none;
  margin: 0;
  padding: 0;
  max-height: 0;
  overflow: hidden;
  transition: max-height 0.3s ease;
  background-color: rgba(0, 0, 0, 0.22);
}

.sub-menu-list.show {
  max-height: 1000px;
  overflow: visible;
}

/* 子菜单项样式 */
.sub-menu-item {
  margin: 0;
  padding: 0;
}

.sub-menu-item.active .sub-menu-link {
  background-color: rgba(142, 17, 3, 0.55);
  color: #fff;
  box-shadow: inset 3px 0 0 var(--primary-200);
}

/* 子菜单链接样式 */
.sub-menu-link {
  display: flex;
  align-items: center;
  padding: 10px 20px 10px 40px;
  color: rgba(255, 255, 255, 0.9);
  text-decoration: none;
  transition: all 0.3s ease;
  white-space: nowrap;
  font-size: 13px;
}

.sub-menu-link:hover {
  background-color: rgba(255, 255, 255, 0.15);
  color: white;
  padding-left: 45px;
}

.sub-menu-link i {
  font-size: 14px;
  margin-right: 8px;
  color: rgba(255, 255, 255, 0.8);
}

/* 自定义滚动条样式 */
.menu-component ::-webkit-scrollbar {
  width: 6px;
}

.menu-component ::-webkit-scrollbar-track {
  background: rgba(255, 255, 255, 0.1);
  border-radius: 3px;
}

.menu-component ::-webkit-scrollbar-thumb {
  background: rgba(255, 255, 255, 0.3);
  border-radius: 3px;
}

.menu-component ::-webkit-scrollbar-thumb:hover {
  background: rgba(255, 255, 255, 0.5);
}

/* 响应式调整 */
@media (max-width: 768px) {
  .menu-component {
    position: fixed;
    top: 80px;
    left: 0;
    bottom: 0;
    z-index: 999;
    box-shadow: var(--shadow);
    height: calc(100% - 80px);
  }
  
  .menu-component:not(.menu-collapsed) {
    width: 240px;
  }
  
  .menu-link {
    padding: 15px 20px;
  }
  
  .menu-link i {
    font-size: 18px;
  }
  
  .menu-text {
    font-size: 16px;
  }

}

@media (min-width: 769px) {
  .menu-component {
    width: 200px;
    flex-shrink: 0;
    height: 100%; /* 确保电脑端菜单高度为100% */
  }
  
  .menu-component.menu-collapsed {
    width: 200px;
  }
  
  /* 确保菜单列表占满整个菜单区域 */
  .menu-list {
    height: 100%;
  }
}
</style>